<template>
    <div class="dbman-page">
        <el-tabs v-model="activeTab" class="dbman-tabs">
            <!-- ==================== 数据库连接 ==================== -->
            <el-tab-pane label="数据库连接" name="conn">
                <div class="dbman-toolbar">
                    <div class="dbman-toolbar__info">
                        <span class="dbman-toolbar__icon">
                            <el-icon>
                                <Coin />
                            </el-icon>
                        </span>
                        <span class="dbman-toolbar__meta">
                            <span class="dbman-toolbar__title">数据库连接</span>
                            <span class="dbman-toolbar__subtitle">维护数据库连接，并配置只读团队 / 读写团队（仅管理员可创建）</span>
                        </span>
                    </div>
                    <div class="dbman-toolbar__actions">
                        <el-button class="dbman-btn dbman-btn--ghost" :loading="connLoading" @click="loadConn">
                            <el-icon v-if="!connLoading" class="dbman-btn__icon">
                                <Refresh />
                            </el-icon>
                            刷新
                        </el-button>
                        <el-button v-if="isSa" class="dbman-btn" type="primary" @click="openCreateConn">
                            <el-icon class="dbman-btn__icon">
                                <Plus />
                            </el-icon>
                            创建连接
                        </el-button>
                    </div>
                </div>

                <section class="dbman-panel" v-loading="connLoading">
                    <el-empty v-if="connList.length === 0 && !connLoading" description="还没有数据库连接" />
                    <template v-else>
                        <section class="conn-group" v-for="group in connGroupList" :key="group.key">
                            <div class="conn-group__head" v-if="showGroupHeader">
                                <span class="conn-group__name">{{ group.name }}</span>
                                <span class="count-pill">{{ group.list.length }} 个</span>
                            </div>
                            <el-collapse accordion class="dbman-collapse" @change="onExpand">
                                <el-collapse-item v-for="item in group.list" :key="item.id" :name="item.id">
                                    <template #title>
                                        <div class="conn-head">
                                            <div class="conn-head__main">
                                                <span class="conn-name">{{ item.name }}</span>
                                                <span class="conn-address">{{ item.host }}:{{ item.port }}/{{ item.defaultDb }}</span>
                                                <el-tag size="small" type="info" effect="plain">{{ item.dbType }}</el-tag>
                                                <el-tag v-if="sqlBlockMap[item.id]" class="conn-cnt" size="small" type="info"
                                                    effect="plain" round>{{ sqlBlockMap[item.id].length }}条SQL</el-tag>
                                                <el-tag v-if="item.prodLock" type="danger" size="small" effect="plain">生产防护</el-tag>
                                            </div>
                                            <div class="conn-head__ops" @click.stop v-if="isSa">
                                                <el-link class="dbman-op" type="primary" @click="openEditConn(item.id)">编辑</el-link>
                                                <el-popconfirm width="240" title="删除操作不可撤回，确定删除该连接吗？"
                                                    confirm-button-text="确定删除" cancel-button-text="取消"
                                                    @confirm="deleteConn(item.id)">
                                                    <template #reference>
                                                        <el-link class="dbman-op" type="danger">删除</el-link>
                                                    </template>
                                                </el-popconfirm>
                                            </div>
                                        </div>
                                    </template>

                                    <div>
                                        <p class="conn-remark">{{ item.remark || '暂无描述' }}</p>
                                        <div class="conn-teams">
                                            <span class="team-label">只读团队：</span>
                                            <el-tag v-for="t in (item.teamList || [])" :key="t.id" size="small" type="info"
                                                effect="plain" class="team-tag">{{ t.name }}</el-tag>
                                            <span v-if="!(item.teamList || []).length" class="team-empty">无</span>
                                            <span class="team-label" style="margin-left:12px;">读写团队：</span>
                                            <el-tag v-for="t in (item.rwTeamList || [])" :key="t.id" size="small" type="warning"
                                                effect="plain" class="team-tag">{{ t.name }}</el-tag>
                                            <span v-if="!(item.rwTeamList || []).length" class="team-empty">无</span>
                                        </div>

                                        <div class="block-block">
                                            <div class="block-block__head">
                                                <span class="block-block__title">SQL 块</span>
                                                <el-button class="dbman-btn dbman-btn--sm" type="primary" plain
                                                    @click="openCreateBlock(item.id)">
                                                    <el-icon class="dbman-btn__icon">
                                                        <Plus />
                                                    </el-icon>
                                                    创建 SQL 块
                                                </el-button>
                                            </div>
                                            <el-table v-loading="blockLoadingMap[item.id]" class="dbman-table"
                                                :data="sqlBlockMap[item.id] || []" stripe style="width: 100%">
                                                <el-table-column prop="name" label="名称" min-width="140"
                                                    show-overflow-tooltip></el-table-column>
                                                <el-table-column prop="description" label="描述" min-width="160"
                                                    show-overflow-tooltip></el-table-column>
                                                <el-table-column label="数据库" width="120" show-overflow-tooltip>
                                                    <template #default="scope">
                                                        <span>{{ scope.row.databaseName || '—' }}</span>
                                                    </template>
                                                </el-table-column>
                                                <el-table-column label="SQL" min-width="220" show-overflow-tooltip>
                                                    <template #default="scope">
                                                        <span class="block-sql">{{ scope.row.sqlText || '—' }}</span>
                                                    </template>
                                                </el-table-column>
                                                <el-table-column label="共享" width="80" align="center">
                                                    <template #default="scope">
                                                        <el-tag size="small" :type="scope.row.isShare ? 'success' : 'info'"
                                                            effect="plain">{{ scope.row.isShare ? '是' : '否' }}</el-tag>
                                                    </template>
                                                </el-table-column>
                                                <el-table-column label="创建者" width="90" align="center">
                                                    <template #default="scope">
                                                        <span v-if="scope.row.ownedByMe">我</span>
                                                        <span v-else class="block-other">他人</span>
                                                    </template>
                                                </el-table-column>
                                                <el-table-column label="操作" width="120">
                                                    <template #default="scope">
                                                        <template v-if="scope.row.ownedByMe">
                                                            <el-link class="dbman-op" type="warning"
                                                                @click="openEditBlock(scope.row)">编辑</el-link>
                                                            <el-popconfirm width="240" title="确定删除该 SQL 块吗？"
                                                                confirm-button-text="确定删除" cancel-button-text="取消"
                                                                @confirm="deleteBlock(scope.row)">
                                                                <template #reference>
                                                                    <el-link class="dbman-op" type="danger">删除</el-link>
                                                                </template>
                                                            </el-popconfirm>
                                                        </template>
                                                        <span v-else class="dbman-op dbman-op--muted">—</span>
                                                    </template>
                                                </el-table-column>
                                                <template #empty>
                                                    <el-empty description="该连接还没有 SQL 块" :image-size="70" />
                                                </template>
                                            </el-table>
                                        </div>
                                    </div>
                                </el-collapse-item>
                            </el-collapse>
                        </section>
                    </template>
                </section>
            </el-tab-pane>

            <!-- ==================== 执行日志 ==================== -->
            <el-tab-pane label="执行日志" name="log">
                <div class="dbman-toolbar">
                    <div class="dbman-toolbar__info">
                        <span class="dbman-toolbar__icon">
                            <el-icon>
                                <Tickets />
                            </el-icon>
                        </span>
                        <span class="dbman-toolbar__meta">
                            <span class="dbman-toolbar__title">执行日志</span>
                            <span class="dbman-toolbar__subtitle">查看数据库 SQL 的历史执行记录</span>
                        </span>
                    </div>
                </div>

                <section class="dbman-panel">
                    <div class="log-search">
                        <div class="log-search__fields">
                            <div class="log-field">
                                <span class="log-field__label">连接名称</span>
                                <el-input v-model="searchDataParams.connName" placeholder="请输入连接名称" clearable
                                    @keydown="searchDataEnter" />
                            </div>
                            <div class="log-field">
                                <span class="log-field__label">语句类型</span>
                                <el-select v-model="searchDataParams.sqlType" placeholder="全部" clearable style="width:100%">
                                    <el-option label="SELECT" value="SELECT" />
                                    <el-option label="INSERT" value="INSERT" />
                                    <el-option label="UPDATE" value="UPDATE" />
                                    <el-option label="DELETE" value="DELETE" />
                                    <el-option label="DDL" value="DDL" />
                                    <el-option label="OTHER" value="OTHER" />
                                </el-select>
                            </div>
                        </div>
                        <div class="log-search__actions">
                            <el-button class="dbman-btn dbman-btn--ghost" @click="resetSearch">重置</el-button>
                            <el-button class="dbman-btn" type="primary" @click="searchData">
                                <el-icon class="dbman-btn__icon">
                                    <Search />
                                </el-icon>
                                搜索
                            </el-button>
                        </div>
                    </div>

                    <el-table v-loading="exeLogLoading" class="dbman-table" :data="exeLogList" stripe style="width: 100%">
                        <el-table-column label="执行时间" width="170">
                            <template #default="scope">{{ scope.row.createTime }}</template>
                        </el-table-column>
                        <el-table-column label="连接" min-width="160" show-overflow-tooltip>
                            <template #default="scope">
                                {{ scope.row.connName }}
                                <span class="log-host">[{{ scope.row.connHost }}]</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="类型" width="90" align="center">
                            <template #default="scope">
                                <el-tag size="small" effect="plain">{{ scope.row.sqlType || scope.row.action }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="SQL" min-width="220" show-overflow-tooltip>
                            <template #default="scope">
                                <span class="block-sql">{{ scope.row.sqlText || '—' }}</span>
                            </template>
                        </el-table-column>
                        <el-table-column label="执行人" width="110">
                            <template #default="scope">{{ scope.row.userRealName }}</template>
                        </el-table-column>
                        <el-table-column label="耗时" width="90" align="right">
                            <template #default="scope">{{ scope.row.elapsedMs }} ms</template>
                        </el-table-column>
                        <el-table-column label="结果" width="80" align="center">
                            <template #default="scope">
                                <el-tag size="small" :type="scope.row.success ? 'success' : 'danger'" effect="plain">
                                    {{ scope.row.success ? '成功' : '失败' }}</el-tag>
                            </template>
                        </el-table-column>
                        <el-table-column label="操作" width="80" align="center">
                            <template #default="scope">
                                <el-link class="dbman-op" type="primary" @click="openLogDetail(scope.row)">详情</el-link>
                            </template>
                        </el-table-column>
                        <template #empty>
                            <el-empty description="暂无执行日志" :image-size="80" />
                        </template>
                    </el-table>
                    <div class="log-pagination">
                        <span class="log-pagination__total">共 {{ exeLogPage.total }} 条</span>
                        <el-pagination background layout="prev, pager, next" :pager-count="5" :total="exeLogPage.total"
                            :current-page="exeLogPage.current" :page-size="exeLogPage.size"
                            @current-change="handleExeLogPageChange" />
                    </div>
                </section>
            </el-tab-pane>
        </el-tabs>

        <!-- 连接表单 -->
        <el-dialog class="dbman-dialog" :title="connForm.mode == 'create' ? '创建数据库连接' : '编辑数据库连接'"
            v-model="connForm.dialog" width="min(640px, 94vw)" :close-on-click-modal="false">
            <el-form :model="connForm.prop" label-width="110px">
                <el-form-item label="名称">
                    <el-input v-model="connForm.prop.name" placeholder="请输入连接名称"></el-input>
                </el-form-item>
                <el-form-item label="分组">
                    <el-input v-model="connForm.prop.groupName" maxlength="64"
                        placeholder="可选，如：客户名称 / 项目名称"></el-input>
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="2" maxlength="200" v-model="connForm.prop.remark"></el-input>
                </el-form-item>
                <el-form-item label="类型">
                    <el-select v-model="connForm.prop.dbType" style="width: 100%" @change="onDbTypeChange">
                        <el-option label="PostgreSQL" value="postgresql"></el-option>
                        <el-option label="MySQL" value="mysql"></el-option>
                    </el-select>
                </el-form-item>
                <el-form-item label="主机地址">
                    <el-input v-model="connForm.prop.host" placeholder="例如 192.168.1.10"></el-input>
                </el-form-item>
                <el-form-item label="端口">
                    <el-input v-model="connForm.prop.port" placeholder="例如 5432"></el-input>
                </el-form-item>
                <el-form-item label="账号">
                    <el-input v-model="connForm.prop.username" name="dbAccount" autocomplete="new-password"></el-input>
                </el-form-item>
                <el-form-item label="密码">
                    <el-input v-model="connForm.prop.password" name="dbPassword" type="password" show-password
                        autocomplete="new-password" placeholder="编辑时留空表示不修改"></el-input>
                </el-form-item>
                <el-form-item label="默认库">
                    <el-input v-model="connForm.prop.defaultDb" placeholder="可空，例如 postgres / order_db"></el-input>
                </el-form-item>
                <el-form-item label="只读团队">
                    <el-select v-model="connForm.prop.teamIdList" multiple placeholder="可选择已加入的团队" style="width: 100%">
                        <el-option v-for="t in team.myJoin" :key="t.id" :label="t.name" :value="t.id" />
                    </el-select>
                    <p class="dbman-tips">团队成员可见该连接，并允许执行查询等只读操作</p>
                </el-form-item>
                <el-form-item label="读写团队">
                    <el-select v-model="connForm.prop.rwTeamIdList" multiple placeholder="可选择已加入的团队" style="width: 100%">
                        <el-option v-for="t in team.myJoin" :key="t.id" :label="t.name" :value="t.id" />
                    </el-select>
                    <p class="dbman-tips">团队成员可见该连接，并允许执行增删改等写操作</p>
                </el-form-item>
                <el-form-item label="生产防护锁">
                    <div class="dbman-switch-row">
                        <el-switch v-model="connForm.prop.prodLock" />
                        <span class="dbman-tips">开启后写操作需输入连接名确认</span>
                    </div>
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button class="dbman-btn dbman-btn--ghost" :loading="testingConn" @click="testConn">测试连接</el-button>
                    <el-button @click="connForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="savingConn" @click="saveConn">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- SQL 块表单 -->
        <el-dialog class="dbman-dialog" :title="blockForm.mode == 'create' ? '创建 SQL 块' : '编辑 SQL 块'"
            v-model="blockForm.dialog" width="min(680px, 94vw)" :close-on-click-modal="false">
            <el-form :model="blockForm.prop" label-width="100px">
                <el-form-item label="名称">
                    <el-input v-model="blockForm.prop.name" placeholder="请输入收藏名称"></el-input>
                </el-form-item>
                <el-form-item label="描述">
                    <el-input type="textarea" :rows="2" maxlength="200" placeholder="可选，便于以后查阅"
                        v-model="blockForm.prop.description"></el-input>
                </el-form-item>
                <el-form-item label="数据库">
                    <el-input v-model="blockForm.prop.databaseName" placeholder="可选，记录执行时使用的数据库名"></el-input>
                </el-form-item>
                <el-form-item label="SQL">
                    <el-input type="textarea" :rows="6" placeholder="请输入 SQL" class="dbman-mono"
                        v-model="blockForm.prop.sqlText"></el-input>
                </el-form-item>
                <el-form-item label="共享使用">
                    <div class="dbman-switch-row">
                        <el-switch v-model="blockForm.prop.isShare" />
                        <span class="dbman-tips">开启后，该连接的只读团队 / 读写团队成员均可查看并执行</span>
                    </div>
                </el-form-item>
                <el-form-item label="排序">
                    <el-input-number v-model="blockForm.prop.orderIndex" :min="0" :max="9999" controls-position="right" />
                </el-form-item>
            </el-form>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button @click="blockForm.dialog = false">取 消</el-button>
                    <el-button type="primary" :loading="savingBlock" @click="saveBlock">保 存</el-button>
                </div>
            </template>
        </el-dialog>

        <!-- 日志详情 -->
        <el-dialog class="dbman-dialog" title="执行详情" v-model="logDialog.dialog" width="min(760px, 94vw)">
            <el-descriptions :column="2" border size="small">
                <el-descriptions-item label="执行时间" :span="2">{{ logDialog.current.createTime }}</el-descriptions-item>
                <el-descriptions-item label="连接">{{ logDialog.current.connName }}</el-descriptions-item>
                <el-descriptions-item label="地址">{{ logDialog.current.connHost }}</el-descriptions-item>
                <el-descriptions-item label="类型">{{ logDialog.current.sqlType }}</el-descriptions-item>
                <el-descriptions-item label="执行人">{{ logDialog.current.userRealName }}</el-descriptions-item>
                <el-descriptions-item label="耗时">{{ logDialog.current.elapsedMs }} ms</el-descriptions-item>
                <el-descriptions-item label="结果">{{ logDialog.current.success ? '成功' : '失败' }}</el-descriptions-item>
                <el-descriptions-item label="SQL" :span="2">
                    <span class="log-script">{{ logDialog.current.sqlText }}</span>
                </el-descriptions-item>
                <el-descriptions-item v-if="logDialog.current.errMsg" label="错误信息" :span="2">
                    <span class="log-script log-script--error">{{ logDialog.current.errMsg }}</span>
                </el-descriptions-item>
            </el-descriptions>
            <template #footer>
                <div class="dbman-dialog__foot">
                    <el-button type="primary" @click="logDialog.dialog = false">关 闭</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import request from '../../../utils/request.js';

// 未填写分组时的兜底分组标识
const UNGROUPED_KEY = '__ungrouped__';

export default {
    data() {
        return {
            activeTab: 'conn',
            isSa: false,
            connList: [],
            connLoading: false,
            savingConn: false,
            testingConn: false,
            sqlBlockMap: {},
            blockLoadingMap: {},
            savingBlock: false,
            currentConnId: '',
            team: { myJoin: [] },
            connForm: {
                mode: 'create',
                dialog: false,
                prop: {
                    id: '',
                    name: '',
                    groupName: '',
                    remark: '',
                    dbType: 'postgresql',
                    host: '',
                    port: 5432,
                    username: '',
                    password: '',
                    defaultDb: '',
                    prodLock: false,
                    teamIdList: [],
                    rwTeamIdList: []
                }
            },
            blockForm: {
                mode: 'create',
                dialog: false,
                prop: {
                    id: '',
                    connId: '',
                    name: '',
                    description: '',
                    sqlText: '',
                    databaseName: '',
                    isShare: false,
                    orderIndex: 0
                }
            },
            exeLogList: [],
            exeLogLoading: false,
            exeLogPage: { current: 1, size: 20, total: 0 },
            searchDataParams: { connName: '', sqlType: '' },
            logDialog: { dialog: false, current: {} }
        };
    },
    computed: {
        connGroupList() {
            const groups = new Map();
            this.connList.forEach(conn => {
                const name = (conn.groupName || '').trim();
                const key = name || UNGROUPED_KEY;
                if (!groups.has(key)) {
                    groups.set(key, { key, name: name || '未分组', list: [] });
                }
                groups.get(key).list.push(conn);
            });
            const named = [];
            let ungrouped = null;
            groups.forEach(group => {
                if (group.key === UNGROUPED_KEY) {
                    ungrouped = group;
                } else {
                    named.push(group);
                }
            });
            named.sort((a, b) => a.name.localeCompare(b.name, 'zh-CN'));
            return ungrouped ? named.concat(ungrouped) : named;
        },
        showGroupHeader() {
            return this.connGroupList.some(group => group.key !== UNGROUPED_KEY);
        }
    },
    mounted() {
        this.isSa = (this.$store.state.user.roles || []).includes('sa');
        this.loadMyJoinTeam();
        this.loadConn();
        this.loadExeLogList();
    },
    methods: {
        loadMyJoinTeam() {
            return request({
                url: '/team/getMyJoinTeams',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.team.myJoin = res.data || [];
                }
            });
        },
        loadConn() {
            this.connLoading = true;
            return request({
                url: '/dbManConn/list',
                method: 'post',
                data: { token: this.$store.state.user.token }
            }).then(res => {
                if (res.code == 0) {
                    this.connList = res.meta.dbConns || [];
                }
            }).finally(() => {
                this.connLoading = false;
            });
        },
        onExpand(connId) {
            if (!connId) return;
            this.currentConnId = connId;
            this.loadSqlBlocks(connId);
        },
        loadSqlBlocks(connId) {
            this.blockLoadingMap[connId] = true;
            return request({
                url: '/dbManSqlBlock/list',
                method: 'post',
                data: { token: this.$store.state.user.token, connId }
            }).then(res => {
                if (res.code == 0) {
                    this.sqlBlockMap[connId] = res.meta.dbSqlBlocks || [];
                }
            }).finally(() => {
                this.blockLoadingMap[connId] = false;
            });
        },
        onDbTypeChange(value) {
            if (value === 'mysql') {
                this.connForm.prop.port = 3306;
            } else {
                this.connForm.prop.port = 5432;
            }
        },
        openCreateConn() {
            this.connForm.mode = 'create';
            this.connForm.prop = {
                id: '', name: '', groupName: '', remark: '', dbType: 'postgresql', host: '', port: 5432,
                username: '', password: '', defaultDb: '', prodLock: false, teamIdList: [], rwTeamIdList: []
            };
            this.connForm.dialog = true;
        },
        openEditConn(id) {
            return request({
                url: '/dbManConn/get',
                method: 'post',
                data: { token: this.$store.state.user.token, id }
            }).then(res => {
                if (res.code == 0 && res.meta.dbConn) {
                    const conn = res.meta.dbConn;
                    this.connForm.prop = {
                        id: conn.id,
                        name: conn.name,
                        groupName: conn.groupName || '',
                        remark: conn.remark || '',
                        dbType: conn.dbType,
                        host: conn.host,
                        port: conn.port,
                        username: conn.username,
                        password: '',
                        defaultDb: conn.defaultDb || '',
                        prodLock: !!conn.prodLock,
                        teamIdList: (conn.teamIdList || []).slice(),
                        rwTeamIdList: (conn.rwTeamIdList || []).slice()
                    };
                    this.connForm.mode = 'edit';
                    this.connForm.dialog = true;
                }
            });
        },
        saveConn() {
            const prop = this.connForm.prop;
            if (!prop.name || !prop.host || !prop.port || !prop.username) {
                ElMessage({ message: '请填写名称、地址、端口与账号', type: 'warning', duration: 2 * 1000 });
                return;
            }
            const isCreate = this.connForm.mode == 'create';
            this.savingConn = true;
            return request({
                url: isCreate ? '/dbManConn/create' : '/dbManConn/edit',
                method: 'post',
                data: Object.assign({ token: this.$store.state.user.token }, prop)
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '保存成功', type: 'success', duration: 2 * 1000 });
                    this.connForm.dialog = false;
                    this.loadConn();
                } else {
                    ElMessage({ message: res.msg || '保存失败', type: 'error', duration: 3 * 1000 });
                }
            }).finally(() => {
                this.savingConn = false;
            });
        },
        deleteConn(id) {
            return request({
                url: '/dbManConn/delete',
                method: 'post',
                data: { token: this.$store.state.user.token, id }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '删除成功', type: 'success', duration: 2 * 1000 });
                    this.loadConn();
                } else {
                    ElMessage({ message: res.msg || '删除失败', type: 'error', duration: 3 * 1000 });
                }
            });
        },
        testConn() {
            const prop = this.connForm.prop;
            const isCreate = this.connForm.mode == 'create';
            const data = { token: this.$store.state.user.token, ...prop };
            // 编辑时若未改密码则不传连接串，直接用 id 测试
            if (!isCreate && !prop.password) {
                delete data.host; delete data.port; delete data.username; delete data.password; delete data.dbType;
            }
            this.testingConn = true;
            return request({
                url: '/dbManConn/test',
                method: 'post',
                data
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '连接成功：' + (res.meta.serverVersion || ''), type: 'success', duration: 3 * 1000 });
                } else {
                    ElMessage({ message: res.msg || '连接失败', type: 'error', duration: 4 * 1000 });
                }
            }).finally(() => {
                this.testingConn = false;
            });
        },
        openCreateBlock(connId) {
            this.blockForm.mode = 'create';
            this.blockForm.prop = {
                id: '', connId, name: '', description: '', sqlText: '', databaseName: '', isShare: false, orderIndex: 0
            };
            this.blockForm.dialog = true;
        },
        openEditBlock(row) {
            this.blockForm.mode = 'edit';
            this.blockForm.prop = {
                id: row.id, connId: row.connId, name: row.name, description: row.description,
                sqlText: row.sqlText, databaseName: row.databaseName || '', isShare: !!row.isShare, orderIndex: row.orderIndex || 0
            };
            this.blockForm.dialog = true;
        },
        saveBlock() {
            const prop = this.blockForm.prop;
            if (!prop.name) {
                ElMessage({ message: '请填写名称', type: 'warning', duration: 2 * 1000 });
                return;
            }
            this.savingBlock = true;
            return request({
                url: '/dbManSqlBlock/save',
                method: 'post',
                data: Object.assign({ token: this.$store.state.user.token }, prop)
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '保存成功', type: 'success', duration: 2 * 1000 });
                    this.blockForm.dialog = false;
                    this.loadSqlBlocks(prop.connId);
                } else {
                    ElMessage({ message: res.msg || '保存失败', type: 'error', duration: 3 * 1000 });
                }
            }).finally(() => {
                this.savingBlock = false;
            });
        },
        deleteBlock(row) {
            return request({
                url: '/dbManSqlBlock/delete',
                method: 'post',
                data: { token: this.$store.state.user.token, id: row.id }
            }).then(res => {
                if (res.code == 0) {
                    ElMessage({ message: '删除成功', type: 'success', duration: 2 * 1000 });
                    this.loadSqlBlocks(row.connId);
                } else {
                    ElMessage({ message: res.msg || '删除失败', type: 'error', duration: 3 * 1000 });
                }
            });
        },
        searchDataEnter(e) {
            if (e.key === 'Enter') this.searchData();
        },
        searchData() {
            this.exeLogPage.current = 1;
            return this.loadExeLogList();
        },
        resetSearch() {
            this.searchDataParams.connName = '';
            this.searchDataParams.sqlType = '';
            return this.searchData();
        },
        handleExeLogPageChange(page) {
            this.exeLogPage.current = page;
            return this.loadExeLogList();
        },
        loadExeLogList() {
            this.exeLogLoading = true;
            return request({
                url: '/dbManExeLog/pageList',
                method: 'post',
                data: {
                    token: this.$store.state.user.token,
                    connName: this.searchDataParams.connName,
                    sqlType: this.searchDataParams.sqlType,
                    current: this.exeLogPage.current,
                    size: this.exeLogPage.size
                }
            }).then(res => {
                if (res.code == 0) {
                    this.exeLogList = res.data || [];
                    this.exeLogPage.total = res.total || 0;
                }
            }).finally(() => {
                this.exeLogLoading = false;
            });
        },
        openLogDetail(row) {
            this.logDialog.current = row;
            this.logDialog.dialog = true;
        }
    }
};
</script>

<style scoped>
.dbman-page {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    --dbman-text: #1f2d3d;
    --dbman-sub: #5b6a80;
    --dbman-muted: #aab4c4;
    --dbman-line: #eef1f6;
    --dbman-border: #e6eaf0;

    color: var(--dbman-text);
}

.dbman-page :deep(.dbman-tabs .el-tabs__header) {
    margin: 0 0 14px;
}

.dbman-page :deep(.dbman-tabs .el-tabs__nav-wrap::after) {
    height: 0;
}

.dbman-page :deep(.dbman-tabs .el-tabs__active-bar) {
    display: none;
}

.dbman-page :deep(.dbman-tabs .el-tabs__item) {
    height: 34px;
    margin-right: 4px;
    padding: 0 16px;
    border-radius: 9px;
    font-size: 13px;
    font-weight: 500;
    line-height: 34px;
    color: var(--dbman-sub);
    transition: background-color 0.2s, color 0.2s;
}

.dbman-page :deep(.dbman-tabs .el-tabs__item.is-active) {
    color: var(--el-color-primary);
    background-color: var(--el-color-primary-light-9);
}

.dbman-toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    row-gap: 10px;
    padding: 10px 16px;
    border: 1px solid #ebeef5;
    border-radius: 12px;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
    box-shadow: 0 10px 28px -22px rgba(31, 45, 61, 0.55);
}

.dbman-toolbar__info {
    display: flex;
    align-items: center;
    min-width: 0;
}

.dbman-toolbar__icon {
    display: flex;
    flex: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-right: 10px;
    border-radius: 10px;
    font-size: 18px;
    color: #fff;
    background: linear-gradient(135deg, #6a9cff 0%, #2f6fed 100%);
    box-shadow: 0 8px 16px -10px rgba(47, 111, 237, 0.9);
}

.dbman-toolbar__meta {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.dbman-toolbar__title {
    font-size: 14px;
    font-weight: 600;
    color: var(--dbman-text);
}

.dbman-toolbar__subtitle {
    font-size: 12px;
    color: #a8abb2;
}

.dbman-toolbar__actions {
    display: flex;
    flex: none;
    flex-wrap: wrap;
    align-items: center;
    gap: 10px;
}

.dbman-btn {
    height: 34px;
    padding: 0 16px;
    border-radius: 9px;
    font-weight: 500;
}

.dbman-btn--ghost {
    color: #606266;
    background-color: #fff;
    border-color: #e4e7ed;
}

.dbman-btn--sm {
    height: 30px;
    padding: 0 12px;
    font-size: 12px;
}

.dbman-btn__icon {
    margin-right: 4px;
}

.dbman-panel {
    margin-top: 12px;
    padding: 14px 16px;
    border: 1px solid var(--dbman-border);
    border-radius: 14px;
    background-color: #fff;
    box-shadow: 0 1px 2px rgba(28, 47, 84, 0.05);
}

.conn-group + .conn-group {
    margin-top: 18px;
}

.conn-group__head {
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
    padding: 8px 12px;
    border: 1px solid var(--el-color-primary-light-7);
    border-left: 3px solid var(--el-color-primary);
    border-radius: 8px;
    background: linear-gradient(90deg, var(--el-color-primary-light-8) 0%, var(--el-color-primary-light-9) 100%);
}

.conn-group__name {
    font-size: 13px;
    font-weight: 700;
    color: var(--el-color-primary-dark-2);
}

.count-pill {
    padding: 1px 8px;
    border: 1px solid var(--el-color-primary-light-7);
    border-radius: 999px;
    background-color: #fff;
    color: var(--el-color-primary);
    font-size: 12px;
}

.dbman-page :deep(.dbman-collapse) {
    border: 0;
}

.dbman-page :deep(.dbman-collapse .el-collapse-item) {
    margin-bottom: 12px;
    border: 1px solid var(--dbman-border);
    border-radius: 12px;
    background-color: #fff;
    overflow: hidden;
}

.dbman-page :deep(.dbman-collapse .el-collapse-item__header) {
    height: 54px;
    padding: 0 16px;
    border-bottom: 0;
    background: linear-gradient(180deg, #ffffff 0%, #fafbfd 100%);
}

.dbman-page :deep(.dbman-collapse .el-collapse-item__wrap) {
    border-bottom: 0;
}

.dbman-page :deep(.dbman-collapse .el-collapse-item__content) {
    padding: 14px 16px 16px;
}

.conn-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    min-width: 0;
}

.conn-head__main {
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    overflow: hidden;
}

.conn-name {
    font-size: 14px;
    font-weight: 600;
    color: var(--dbman-text);
    white-space: nowrap;
}

.conn-address {
    font-size: 12px;
    color: var(--dbman-muted);
    white-space: nowrap;
}

.conn-head__ops {
    display: flex;
    flex: none;
    align-items: center;
    gap: 14px;
    margin-right: 10px;
}

.conn-remark {
    margin: 0 0 8px;
    font-size: 13px;
    color: var(--dbman-sub);
}

.conn-teams {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding-bottom: 12px;
    font-size: 12px;
}

.team-label {
    color: #8a94a6;
}

.team-tag {
    margin: 0;
}

.team-empty {
    color: var(--dbman-muted);
}

.block-block {
    padding-top: 12px;
    border-top: 1px dashed var(--dbman-line);
}

.block-block__head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
}

.block-block__title {
    position: relative;
    padding-left: 10px;
    font-size: 13px;
    font-weight: 600;
    color: var(--dbman-text);
}

.block-block__title::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    width: 3px;
    height: 13px;
    margin-top: -6.5px;
    border-radius: 2px;
    background: linear-gradient(180deg, #6a9cff, #3f6fff);
}

.block-block__head .dbman-btn {
    margin-left: auto;
}

.dbman-panel :deep(.dbman-table) {
    --el-table-header-bg-color: #f7f9fc;
    font-size: 13px;
}

.block-sql,
.log-script {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    color: var(--dbman-sub);
}

.log-script {
    white-space: pre-wrap;
    word-break: break-all;
}

.log-script--error {
    color: #f56c6c;
}

.block-other {
    color: var(--dbman-muted);
}

.dbman-op {
    margin-right: 14px;
    font-size: 13px;
}

.dbman-op:last-child {
    margin-right: 0;
}

.dbman-op--muted {
    color: var(--dbman-muted);
}

.log-host {
    color: #909399;
    font-size: 12px;
}

.log-search {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 12px;
    padding-bottom: 14px;
    margin-bottom: 14px;
    border-bottom: 1px dashed var(--dbman-line);
}

.log-search__fields {
    display: grid;
    flex: 1 1 420px;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
}

.log-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    min-width: 0;
}

.log-field__label {
    font-size: 12px;
    color: #8a94a6;
}

.log-search__actions {
    display: flex;
    flex: none;
    gap: 10px;
}

.log-pagination {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    margin-top: 16px;
}

.log-pagination__total {
    font-size: 12px;
    color: #8a94a6;
}
</style>

<!-- 弹窗会 teleport 到 body，样式需放在非 scoped 块并带唯一前缀 -->
<style>
.dbman-dialog {
    --el-color-primary: #2f6fed;
    --el-color-primary-light-3: #6d9af2;
    --el-color-primary-light-5: #97b7f6;
    --el-color-primary-light-7: #c1d4fa;
    --el-color-primary-light-8: #d5e2fb;
    --el-color-primary-light-9: #eaf1fd;
    --el-color-primary-dark-2: #2659be;

    max-width: calc(100vw - 32px);
    border-radius: 14px;
}

.dbman-dialog .el-dialog__header {
    padding: 22px 24px 0;
}

.dbman-dialog .el-dialog__title {
    font-size: 17px;
    font-weight: 600;
    color: #1f2d3d;
}

.dbman-dialog .el-dialog__body {
    padding: 18px 24px 4px;
}

.dbman-dialog .el-dialog__footer {
    padding: 0 24px 22px;
}

.dbman-dialog .el-form-item {
    margin-bottom: 18px;
}

.dbman-dialog .el-form-item__label {
    font-size: 13px;
    font-weight: 500;
    color: #8a94a6;
}

.dbman-dialog .dbman-switch-row {
    display: flex;
    align-items: center;
    gap: 10px;
}

.dbman-dialog .dbman-tips {
    margin: 6px 0 0;
    font-size: 12px;
    color: #aab4c4;
}

.dbman-dialog .dbman-switch-row .dbman-tips {
    margin: 0;
}

.dbman-dialog .dbman-mono textarea {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    font-size: 12px;
    line-height: 18px;
}

.dbman-dialog__foot {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.dbman-dialog__foot .el-button {
    min-width: 96px;
    height: 38px;
    margin-left: 0;
    border-radius: 10px;
}
</style>
